import type { en } from "./en.ts";

export const fr: typeof en = {
    EditSegmentPage: {
        liveRegion: { loading: "Chargement du segment" }
    },
    SegmentForm: {
        createTitle: "Nouveau segment",
        editTitle: "Modifier le segment",
        name: {
            label: "Nom du segment",
            placeholder: "Nommez ce segment",
            required: "Entrez un nom pour ce segment.",
            tooLong: "Limitez le nom à moins de {{count}} caractères."
        },
        save: "Enregistrer",
        discard: "Annuler les modifications",
        delete: "Supprimer",
        leaveConfirm: {
            title: "Abandonner les modifications non enregistrées?",
            description: "Vous avez des modifications non enregistrées sur cette page. Si vous quittez maintenant, elles seront abandonnées.",
            leave: "Abandonner les modifications",
            stay: "Rester sur la page"
        },
        deleteConfirm: {
            title: "Supprimer « {{name}} »?",
            description: "Le segment sera retiré pour toutes les personnes avec qui il est partagé. Les personnes qui en font partie et leurs données ne sont pas touchées. Cette action est irréversible.",
            cancel: "Annuler"
        },
        modeConfirm: {
            title: "Passer aux règles?",
            description: "Un segment est soit basé sur des règles, soit composé de personnes précises. Passer aux règles supprime les personnes que vous avez ajoutées.",
            confirm: "Passer aux règles et supprimer",
            cancel: "Conserver la liste"
        },
        createdToast: "Segment créé",
        savedToast: "Modifications enregistrées",
        deletedToast: "Segment supprimé",
        saveErrorToast: "Impossible d'enregistrer ce segment. Réessayez.",
        deleteErrorToast: "Impossible de supprimer ce segment. Réessayez.",
        rules: {
            loading: "Chargement des champs des employés",
            error: "Impossible de charger vos champs des employés.",
            retry: "Réessayer"
        }
    },
    segmentRules: {
        heading: "Règles",
        matchLabel: "Correspondre à",
        matchSuffix: {
            all: "les conditions suivantes",
            any: "des conditions suivantes"
        },
        match: {
            all: "toutes",
            any: "au moins une"
        },
        columns: {
            property: "Propriété",
            condition: "Condition",
            value: "Valeur"
        },
        addRule: "Ajouter une règle",
        removeRule: "Supprimer la règle",
        property: {
            placeholder: "Sélectionner une propriété",
            unavailable: "Ce champ n'est plus disponible"
        },
        condition: {
            placeholder: "Sélectionner une condition",
            unsupported: "Cette règle utilise une condition qui ne peut pas être modifiée ici. Choisissez-en une autre ou supprimez la règle."
        },
        value: {
            placeholder: "Sélectionner une valeur",
            personPlaceholder: "Rechercher des personnes",
            personNoResults: "Aucune personne trouvée",
            personDeactivatedLabel: "{{name}} (Désactivé)",
            personResolving: "Chargement…",
            optionNoResults: "Aucune option trouvée",
            selectedPeople: "Personnes sélectionnées",
            selectedOptions: "Valeurs sélectionnées",
            rangeStart: "Début",
            rangeEnd: "Fin",
            periodCount: "Nombre",
            periodType: "Unité",
            periodTypePlaceholder: "Sélectionner une unité",
            periodTypes: {
                Years: "Années",
                Months: "Mois",
                Days: "Jours"
            }
        },
        conditions: {
            is: "est",
            isExactly: "est exactement",
            isNot: "n'est pas",
            isAnyOf: "est l'une de",
            isNoneOf: "n'est aucune de",
            isBefore: "est avant",
            isAfter: "est après",
            isGreaterThan: "est supérieur à",
            isLessThan: "est inférieur à",
            isBetween: "est entre",
            isOlderThan: "survenu il y a plus de",
            isWithinTheLast: "survenu il y a moins de",
            occurredBetween: "survenu entre",
            occursInMoreThan: "survient dans plus de",
            occursInLessThan: "survient dans moins de",
            occursInBetween: "survient entre",
            directlyTo: "directement de",
            upThrough: "à tout niveau de"
        },
        rangeConnector: "et",
        loadingRules: "Chargement des détails du filtre",
        ruleUnavailable: "{{field}} : filtre indisponible",
        unlistedRules: "Certaines règles de ce segment ne peuvent pas être affichées ici. L'enregistrement les supprimera.",
        incomplete: "Complétez cette règle avant d'enregistrer.",
        missingRangeEnd: "Saisissez un début et une fin.",
        requiredRule: {
            ownLabel: "Votre structure hiérarchique",
            ownTitle: "Toute votre équipe",
            ownDescription: "S'applique toujours. Limite ce segment aux personnes que vous gérez.",
            ownHint: "Ajoutez une règle pour cibler au sein de votre équipe.",
            otherLabel: "Structure hiérarchique du propriétaire",
            otherTitle: "Toute l'équipe de {{name}}",
            otherTitleFallback: "Toute l'équipe du propriétaire",
            otherDescription: "S'applique toujours. Limite ce segment aux personnes qu'ils gèrent.",
            unlisted: "Ce segment est restreint par une règle supplémentaire qui ne peut pas être affichée ici."
        },
        valuesError: "Impossible de charger les options de ce champ.",
        peopleError: "Impossible de charger les personnes.",
        retry: "Réessayer",
        noUsableFields: "Aucun de vos champs des employés ne peut encore servir dans une règle. Ajoutez un champ de type liste déroulante, date, nombre ou personne dans Champs des employés."
    },
    SegmentMemberPreview: {
        matchSuffix_one: "personne correspond actuellement",
        matchSuffix_other: "personnes correspondent actuellement",
        empty: "Personne ne correspond encore à ces règles.",
        loading: "Vérification des correspondances...",
        error: "Impossible de vérifier les correspondances.",
        retry: "Réessayer",
        staleError: "Impossible d'actualiser — affichage des derniers résultats.",
        liveRegion: {
            loadedMore_one: "{{count}} personne supplémentaire chargée",
            loadedMore_other: "{{count}} personnes supplémentaires chargées",
            refreshed_one: "{{count}} personne correspond actuellement",
            refreshed_other: "{{count}} personnes correspondent actuellement"
        }
    },
    SegmentModeChooser: {
        heading: "Comment les personnes sont ajoutées",
        selected: "Sélectionné",
        ruleBased: {
            title: "Basé sur des règles",
            description: "Se met à jour automatiquement selon vos règles."
        },
        handPicked: {
            title: "Personnes précises",
            description: "Choisissez qui en fait partie. La liste reste fixe."
        }
    },
    segmentTags: {
        heading: "Étiquettes",
        placeholder: "Ajouter des étiquettes",
        suggestedLabel: "Suggestions :",
        suggestions: {
            analytics: "Analytique",
            survey: "Sondage",
            reviewCycle: "Cycle d'évaluation",
            onboarding: "Intégration",
            compensation: "Rémunération"
        },
        remove: "Retirer l'étiquette {{tag}}",
        duplicate: "Cette étiquette est déjà ajoutée.",
        suggestionsUpdated_one: "{{count}} étiquette suggérée",
        suggestionsUpdated_other: "{{count}} étiquettes suggérées"
    },
    segmentMembers: {
        heading: "Personnes",
        count_one: "{{count}} personne",
        count_other: "{{count}} personnes",
        remove: "Retirer {{name}}",
        empty: "Aucune personne ajoutée.",
        required: "Ajoutez au moins une personne à ce segment.",
        search: {
            label: "Rechercher des personnes à ajouter",
            placeholder: "Rechercher des personnes à ajouter...",
            loading: "Recherche en cours...",
            loadingMore: "Chargement en cours...",
            noResults: "Aucune personne ne correspond à « {{term}} ».",
            error: "Impossible de rechercher des personnes. Réessayez.",
            liveRegion: {
                loadedMore_one: "{{count}} personne supplémentaire chargée",
                loadedMore_other: "{{count}} personnes supplémentaires chargées"
            }
        }
    },
    SegmentNotFound: {
        heading: "Ce segment n'existe pas ou n'est pas partagé avec vous.",
        backToSegments: "Retour aux segments"
    }
};
